import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ProgressBar from "components/ProgressBar";
import Button from "components/Button";
import Dropdown from "components/Dropdown";
import ModalGeneralSuccessfully from "screens/ModalGeneralSuccessfully";
import ModalGeneralFailed from "screens/ModalGeneralFailed";
//import ModalPartialAuthorizationAccepted from "screens/ModalPartialAuthorizationAccepted";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import {
  GET_ALL_AVAILABLE_DEVICES,
  MAKE_PAYMENT_BY_GROUP_ID,
  GET_PAYMENT_TERMINAL_REQUEST_BY_ID
} from "graphql/service";
import { Head, Container, Content, Footer, StateContainer } from "./styles";
import { PaymentTerminalIcon } from "assets/icons";
import { debouncedErrorMessage } from "utils/messageUtil";

const Payment = ({status, setStatus}) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const groupId = useParams().groupId;
  const { socketId, connected } = useSelector((state) => state.socketReducer);

  const [method, setMethod] = useState("");
  const [makePaymentId, setMakePaymentId] = useState(null);
  const [makePaymentLoading, setMakePaymentLoading] = useState(false);
  const [paymentSuccessfullyIsOpen, setPaymentSuccessfullyIsOpen] = useState(false);
  const [paymentFailedIsOpen, setPaymentFailedIsOpen] = useState(false);
  // const [partialAuthorizationIsOpen, setPartialAuthorizationIsOpen] = useState(false);
  // const [credit, setCredit] = useState(0);
  // const [balance, setBalance] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  const { data: deviceData, error: deviceError } = useQuery(GET_ALL_AVAILABLE_DEVICES);

  const options = useMemo(() => {
    const deviceOptions = deviceData?.devices?.map((device) => ({
      label: device.name,
      value: device.deviceId,
    })) ?? [];

    return [...deviceOptions, { label: "Manual Entry", value: "Manual Entry" }];
  }, [deviceData]);

  const [getPaymentTerminalRequestByID,
    { startPolling, stopPolling, data: paymentData, error: paymentError }] = useLazyQuery(GET_PAYMENT_TERMINAL_REQUEST_BY_ID, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (!paymentData?.getPaymentTerminalRequestByID) return;

    const { status } = paymentData.getPaymentTerminalRequestByID;

    if (status === "SUCCESS") {
      setPaymentFailedIsOpen(false);
      setPaymentSuccessfullyIsOpen(true);
      stopPolling();
    } else if (status === "PENDING") {
      setPaymentFailedIsOpen(false);
      // keep polling running
    } else {
      stopPolling();
      // optionally handle failed or unknown status
    }
  }, [paymentData, stopPolling]);

  useEffect(() => {
    if (!deviceError || !paymentError) return;

    if (!deviceError.networkError && !paymentError.networkError) {
      let content = deviceError.message || paymentError.message;

      if (deviceError.message.includes('Validation') || paymentError.message.includes('Validation')) {
        content = JSON.stringify(
          deviceError.graphQLErrors[0]?.extensions?.exception || paymentError.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [deviceError, paymentError]);

  const [makePayment, { loading }] = useMutation(MAKE_PAYMENT_BY_GROUP_ID, {
    onCompleted: (data) => {
      setMakePaymentId(data.makePayment?.makePaymentId);
      setMakePaymentLoading(true);
    },
    onError: (error) => {
      setMakePaymentLoading(false);
      setPaymentFailedIsOpen(true);
      setErrorMessage(error.message);
    },
  });

  useEffect(() => {
    if (!connected) return; 

    const handlePaymentComplete = (data) => {
      if(data?.status === "SUCCESS") {
        setPaymentFailedIsOpen(false);
        setPaymentSuccessfullyIsOpen(true);
      } else {
        setPaymentFailedIsOpen(true);
        setErrorMessage(data?.message);
      }
      stopPolling();
    };

    const handlePaymentFailed = (data) => {
      setPaymentFailedIsOpen(true);
      setErrorMessage(data?.message);
      stopPolling();
    };

    dispatch({
      type: 'socket/on',
      payload: { event: 'PAYMENT_TRANSACTION_COMPLETE', callback: handlePaymentComplete }
    });

    dispatch({
      type: 'socket/on',
      payload: { event: 'PAYMENT_TRANSACTION_FAILED', callback: handlePaymentFailed }
    });

    return () => {
      dispatch({
        type: 'socket/off',
        payload: { event: 'PAYMENT_TRANSACTION_COMPLETE' }
      });

      dispatch({
        type: 'socket/off',
        payload: { event: 'PAYMENT_TRANSACTION_FAILED' }
      });
    };
  }, [dispatch, connected, stopPolling]);


  useEffect(() => {
    if (!makePaymentId) return;

    getPaymentTerminalRequestByID({ variables: { id: makePaymentId } });
    startPolling(10000);
    return () => {
      stopPolling();
    };
  }, [makePaymentId, getPaymentTerminalRequestByID, startPolling, stopPolling]);

  const handleSend = () => {
    if (makePaymentLoading || loading) return;
    if (method === "") {
      const content = "Please select a method";
      debouncedErrorMessage(content);
    } else if (method === "Manual Entry") {
      navigate(`/group-check-in/credit-card/${groupId}`);
    } else {
      //setPartialAuthorizationIsOpen(false);
      setPaymentFailedIsOpen(false);
      setPaymentSuccessfullyIsOpen(false);
      makePayment({
        variables: {
          input: {
            groupId: groupId,
            deviceId: method,
            pmsSocketId: socketId,
          },
        },
      });
    }
  };

  return (
    <Container>
      <ProgressBar step={3} groupId={groupId} status={status}/>
      {makePaymentLoading ? (
        <StateContainer>
          <div className="instruction">
            <div className="icon">
              <PaymentTerminalIcon />
            </div>
            <div className="title">
              <div>
                {`Follow the instructions on the payment terminal at ${options.find((option) => option.value === method)?.label}.`}
              </div>
            </div>
          </div>
        </StateContainer>
      ) : (
        <>
          <Content>
            <Head>Payment Device</Head>
            <div className="text">
              <div>Which payment terminal would you like to use?</div>
            </div>
            <Dropdown
              mainPage
              options={options}
              value={
                options.find((option) => option.value === method)?.label ||
                "- Select -"
              }
              title="PREFERRED DEVICE"
              onChange={(e) => {
                setMethod(e);
              }}
            />
          </Content>
          <Footer>
            <div className="buttonContainer">
              <Button
                mainPage
                success
                onClick={handleSend}
                data-loading={loading}
                additionalstyle={`
              width: 72px;
            `}
              >
                {loading ? null : "Send"}
              </Button>
            </div>
          </Footer>
        </>
      )}
      <ModalGeneralSuccessfully
        isOpen={paymentSuccessfullyIsOpen}
        setIsOpen={setPaymentSuccessfullyIsOpen}
        type="checkInPayment"
        callback={() => setStatus("CHECKED_IN")}
      />
      <ModalGeneralFailed
        isOpen={paymentFailedIsOpen}
        setIsOpen={setPaymentFailedIsOpen}
        type="checkInPayment"
        errorType={errorMessage}
        retryFunction={handleSend}
        cancelFunction={() => setMakePaymentLoading(false)}
      />
      {/* <ModalPartialAuthorizationAccepted
        isOpen={partialAuthorizationIsOpen}
        setIsOpen={setPartialAuthorizationIsOpen}
        amount={credit}
        balance={balance}
        retryFunction={handleSend}
      /> */}
    </Container>
  );
};

export default Payment;
